import { useMutation, useQuery, useQueryClient } from "react-query";
import { API_URL, APIError, APIResponse, get, post } from "../api";
import { useAuth } from "../context/AuthContext";

export interface GeoResolution {
  detected_country: string | null;
  detected_provider: string | null;
  residence_country: string | null;
  residence_source: string | null;
  is_international: boolean;
  is_declared: boolean;
}

/**
 * Which country the caller appears to be in, and which residency is recorded
 * against their account.
 *
 * Public on the backend, because the signup form needs a country default before
 * an account exists — that is the whole reason it is not behind `auth:api`. The
 * token is still sent when there is one, since an authenticated caller gets
 * their recorded residency back rather than only the detected country.
 *
 * Never let this block a screen. Detection can legitimately return null (an IP
 * that cannot be placed, a vendor timing out), and the caller's job is to fall
 * back to its own default rather than wait or error. `country` below is
 * therefore `string | null` and every consumer must handle the null.
 */
function useResolveGeo(enabled = true) {
  const { user } = useAuth();
  const query = useQuery<APIResponse<GeoResolution>, APIError>({
    queryKey: ["geo-resolve", user?.id ?? (localStorage.getItem("token") ? "authenticated" : "public")],
    queryFn: () => {
      const token = localStorage.getItem("token");
      return get(
        token ? `${API_URL}/account/compliance/residence` : `${API_URL}/geo/resolve`,
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
      );
    },
    enabled,
    // A caller's country does not change within a session in any way that
    // should re-drive the form under them.
    staleTime: 15 * 60_000,
    retry: 1,
  });

  const data = query.data?.data;

  return {
    /** Recorded residency when known, otherwise what detection saw. */
    country: data?.residence_country ?? data?.detected_country ?? null,
    detectedCountry: data?.detected_country ?? null,
    residenceCountry: data?.residence_country ?? null,
    /** True only after a declared non-Nigerian residence; unknown reads as false. */
    isInternational: data?.is_international ?? false,
    /** True once the customer has answered for themselves — stop asking. */
    isDeclared: data?.is_declared ?? false,
    isResolvingGeo: query.isLoading,
  };
}

export default useResolveGeo;

export function useSetResidence(options?: { onSuccess?: () => void; onError?: (error: APIError) => void }) {
  const queryClient = useQueryClient();
  const mutation = useMutation<APIResponse<any>, APIError, { country: string }>(
    (variables) => post(`${API_URL}/account/compliance/residence`, variables, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("geo-resolve");
        queryClient.invalidateQueries("kyc-requirements");
        options?.onSuccess?.();
      },
      onError: options?.onError,
    }
  );

  return { setResidence: mutation.mutate, savingResidence: mutation.isLoading };
}
